import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai';
import { aiAuthHeaders, assistantChatUrl } from '@/api/ai/ai.service';
import type {
  AskUserInput,
  AskUserOutput,
  AssistantConversationMessage,
  AssistantUIMessage,
} from '@/api/ai/ai.types';

/** The four lifecycle states the AI SDK reports for a chat. */
export type ChatStatus = 'submitted' | 'streaming' | 'ready' | 'error';

/** A pending `askUser` tool call awaiting the user's answers. */
export interface PendingAskUser {
  toolCallId: string;
  input: AskUserInput;
}

export interface UseAssistantChat {
  messages: AssistantUIMessage[];
  status: ChatStatus;
  error: Error | undefined;
  /** True while a request is in flight (`submitted` or `streaming`). */
  isBusy: boolean;
  /** Persisted conversation id, or `null` for a brand-new (unsent) chat. */
  conversationId: string | null;
  /** A pending askUser tool call that needs the user's answers, if any. */
  pendingAskUser: PendingAskUser | null;
  /** Sends a user message; ignored while busy or when `text` is blank. */
  send: (text: string) => void;
  /** Submits the user's answers for a pending `askUser` tool call. */
  submitAskUserAnswers: (output: AskUserOutput) => void;
  /**
   * Wall-clock duration of each completed assistant turn, keyed by assistant
   * message id. Populated when `status` transitions back to `'ready'`; used by
   * the panel to render an end-of-turn footer.
   */
  turnDurationsMs: Record<string, number>;
  /** Aborts the in-flight generation, keeping the tokens received so far. */
  stop: () => void;
  /** Regenerates the last assistant turn after an error. */
  retry: () => void;
  /**
   * Clears the conversation locally and starts a brand-new one — the next
   * `send` will mint a fresh `conversationId` on the backend. The previous
   * conversation stays persisted in `ai_conversations`.
   */
  newConversation: () => void;
  /**
   * Switches the chat to an existing persisted conversation. Pass the
   * conversation id and the rehydrated message list (from
   * `GET /v1/ai/conversations/:id`).
   */
  loadConversation: (
    id: string,
    messages: AssistantConversationMessage[],
  ) => void;
}

/**
 * Hydrates persisted DB messages into the AI SDK `UIMessage` shape `useChat`
 * expects. `parts` and `metadata` round-trip verbatim — the backend stores
 * them as JSONB — so this is a thin re-key.
 */
function hydrate(
  message: AssistantConversationMessage,
): AssistantUIMessage {
  return {
    id: message.clientMessageId ?? message.id,
    role: message.role,
    parts: message.parts,
    metadata: {
      ...(message.metadata ?? {}),
      // Server-issued message id used by the rating endpoint.
      messageId: message.id,
      rating: message.rating,
    },
  } as AssistantUIMessage;
}

/**
 * Drives the EOS Assistant chat: a real streaming `useChat` conversation
 * against `POST /v1/ai/chat`. Catalog and business-partner tools run on the
 * backend. The `askUser` tool is **client-executed**: the model calls it to
 * present structured questions, the UI renders interactive option cards, and
 * the user's answers are sent back as the tool result to resume the turn.
 *
 * Conversations are **persisted** on the backend: the first send creates a
 * fresh `ai_conversations` row (the server emits its id on the `start` part's
 * metadata, which we capture into `conversationId`), and every subsequent
 * send carries that id so the same conversation grows. Switching to a stored
 * conversation goes through {@link UseAssistantChat.loadConversation}.
 */
export function useAssistantChat(): UseAssistantChat {
  const [pendingAskUser, setPendingAskUser] = useState<PendingAskUser | null>(
    null,
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  // Mirror `conversationId` into a ref so the transport's
  // `prepareSendMessagesRequest` callback (created once, never re-bound) can
  // always read the freshest id without forcing a re-memo of the transport.
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  const chatRef = useRef<ReturnType<typeof useChat<AssistantUIMessage>>>(null);

  // `prepareSendMessagesRequest` runs at fetch time, not render time — the
  // lint rule cannot prove that, hence the targeted disable for the ref read.
  /* eslint-disable react-hooks/refs -- callback runs at fetch time, not during render */
  const transport = useMemo(
    () =>
      new DefaultChatTransport<AssistantUIMessage>({
        api: assistantChatUrl(),
        prepareSendMessagesRequest: ({ messages }) => ({
          headers: aiAuthHeaders(),
          body: {
            messages,
            conversationId: conversationIdRef.current ?? undefined,
          },
        }),
      }),
    [],
  );
  /* eslint-enable react-hooks/refs */

  const [turnDurationsMs, setTurnDurationsMs] = useState<
    Record<string, number>
  >({});
  const turnStartedAtRef = useRef<number | null>(null);
  const prevStatusRef = useRef<ChatStatus>('ready');

  const chat = useChat<AssistantUIMessage>({
    transport,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onToolCall({ toolCall }) {
      if (toolCall.dynamic || toolCall.toolName !== 'askUser') return;
      setPendingAskUser({
        toolCallId: toolCall.toolCallId,
        input: toolCall.input,
      });
    },
  });
  // Mirror the `useChat` handle into a ref so `submitAskUserAnswers` (a
  // useCallback) can reach `addToolOutput` without taking the whole `chat`
  // value as a dep — the AI SDK re-creates the object every render.
  useEffect(() => {
    chatRef.current = chat;
  });

  const { messages, status, error, sendMessage, stop, regenerate, setMessages } =
    chat;

  // Watch the freshest assistant message for a server-issued conversationId.
  // The backend emits it on the `start` metadata of every turn, so we grab
  // it eagerly — useful both on the first turn (none was set client-side)
  // and as a defence against drift if it ever changed mid-session.
  useEffect(() => {
    if (status !== 'streaming' && status !== 'submitted') return;
    const lastAssistant = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant');
    const incoming = lastAssistant?.metadata?.conversationId;
    if (incoming && incoming !== conversationIdRef.current) {
      setConversationId(incoming);
    }
  }, [messages, status]);

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || status === 'submitted' || status === 'streaming') return;
      turnStartedAtRef.current = Date.now();
      void sendMessage({ text: trimmed });
    },
    [sendMessage, status],
  );

  // Record turn duration when the assistant finishes streaming. We attach it to
  // the id of the most recent assistant message so the footer can render under
  // the right turn — multiple completed turns each get their own timing.
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    const wasBusy = prev === 'submitted' || prev === 'streaming';
    if (!wasBusy || status !== 'ready') return;
    const startedAt = turnStartedAtRef.current;
    turnStartedAtRef.current = null;
    if (startedAt == null) return;
    const lastAssistant = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant');
    if (!lastAssistant) return;
    const duration = Date.now() - startedAt;
    setTurnDurationsMs((current) => ({
      ...current,
      [lastAssistant.id]: duration,
    }));
  }, [status, messages]);

  const submitAskUserAnswers = useCallback((output: AskUserOutput) => {
    const pending = pendingAskUser;
    if (!pending || !chatRef.current) return;
    setPendingAskUser(null);
    chatRef.current.addToolOutput({
      tool: 'askUser',
      toolCallId: pending.toolCallId,
      output,
    });
  }, [pendingAskUser]);

  const retry = useCallback(() => {
    void regenerate();
  }, [regenerate]);

  const newConversation = useCallback(() => {
    stop();
    setMessages([]);
    setPendingAskUser(null);
    setTurnDurationsMs({});
    turnStartedAtRef.current = null;
    setConversationId(null);
    conversationIdRef.current = null;
  }, [stop, setMessages]);

  const loadConversation = useCallback(
    (id: string, persisted: AssistantConversationMessage[]) => {
      stop();
      setPendingAskUser(null);
      setTurnDurationsMs({});
      turnStartedAtRef.current = null;
      setConversationId(id);
      conversationIdRef.current = id;
      setMessages(persisted.map(hydrate));
    },
    [stop, setMessages],
  );

  return {
    messages,
    status,
    error,
    isBusy: status === 'submitted' || status === 'streaming',
    conversationId,
    pendingAskUser,
    turnDurationsMs,
    send,
    submitAskUserAnswers,
    stop,
    retry,
    newConversation,
    loadConversation,
  };
}
